/*
 * vd-article-with-toc
 * - Builds the "In this article" rail from the <h2> headings in the article body
 *   (a heading's data-toc attribute overrides its label), with scroll-spy.
 * - Wires the FAQ accordion, references toggle, testimonial slider, share menu
 *   and the sticky shop bar used by the article HTML.
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function slugify(text) {
    return text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  // textContent drops <br>s, gluing words together ("Minoxidil:What's")
  function headingText(heading) {
    var clone = heading.cloneNode(true);
    // ignore the mobile-only copy added by initMobileHeadings
    clone.querySelectorAll('.vd-awt-h-mobile').forEach(function (el) {
      el.remove();
    });
    clone.querySelectorAll('br').forEach(function (br) {
      br.replaceWith(' ');
    });
    return clone.textContent;
  }

  function normalise(text) {
    return String(text)
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/\u00b7/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Mobile line breaks from the "Mobile heading lines" setting: one heading per line,
  // its mobile lines separated by " | ". Matched by text, so it doesn't depend on any
  // <br>/class surviving the article editor. Desktop keeps the original heading.
  function initMobileHeadings(root) {
    var el = root.querySelector('[data-vd-awt-mobile-headings]');
    if (!el) return;
    var spec;
    try {
      spec = String(JSON.parse(el.textContent) || '');
    } catch (e) {
      return;
    }
    var rules = spec
      .split(/\r?\n/)
      .map(function (line) {
        return line.split('|').map(function (part) {
          return part.trim();
        }).filter(Boolean);
      })
      .filter(function (parts) {
        return parts.length > 1;
      });
    if (!rules.length) return;

    root.querySelectorAll('.vd-awt-content h2, .vd-awt-content h3, .vd-awt-content .vd-awt-product__desc, .vd-awt-content .vd-awt-product__small').forEach(function (heading) {
      if (heading.querySelector('.vd-awt-h-mobile')) return;
      var text = normalise(headingText(heading));
      var parts = rules.find(function (p) {
        return normalise(p.join(' ')) === text;
      });
      if (!parts) return;

      var em = heading.querySelector('em, i');
      var emText = em ? em.textContent.replace(/\s+/g, ' ').trim() : '';
      var emNorm = normalise(emText);

      var mobile = parts
        .map(function (part) {
          var partNorm = normalise(part);
          var html = escapeHtml(part);
          if (emNorm && emNorm.indexOf(partNorm) !== -1) {
            html = '<em>' + html + '</em>';
          } else if (emNorm && partNorm.indexOf(emNorm) !== -1) {
            var at = partNorm.indexOf(emNorm);
            html = escapeHtml(part.slice(0, at)) + '<em>' + escapeHtml(part.slice(at, at + emText.length)) + '</em>' + escapeHtml(part.slice(at + emText.length));
          }
          return '<span>' + html + '</span>';
        })
        .join('');

      var desktop = document.createElement('span');
      desktop.className = 'vd-awt-h-desktop';
      while (heading.firstChild) desktop.appendChild(heading.firstChild);
      var mob = document.createElement('span');
      mob.className = 'vd-awt-h-mobile';
      mob.setAttribute('aria-hidden', 'true');
      mob.innerHTML = mobile;
      heading.appendChild(desktop);
      heading.appendChild(mob);
    });
  }

  function stickyTop(root) {
    return parseFloat(getComputedStyle(root).getPropertyValue('--vd-awt-sticky-top')) || 140;
  }

  function scrollToTarget(target) {
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function initToc(root) {
    var nav = root.querySelector('[data-vd-awt-toc]');
    var list = nav && nav.querySelector('[data-vd-awt-toc-list]');
    var content = root.querySelector('[data-vd-awt-content]');
    if (!list || !content) return null;

    var headings = Array.prototype.filter.call(content.querySelectorAll('h2'), function (h) {
      return h.textContent.trim() && !h.hasAttribute('data-toc-skip');
    });
    if (!headings.length) return null;

    // Section setting: one "part of heading => menu label" per line
    var overrides = [];
    var overridesEl = root.querySelector('[data-vd-awt-toc-labels]');
    if (overridesEl) {
      try {
        String(JSON.parse(overridesEl.textContent) || '')
          .split(/\r?\n/)
          .forEach(function (line) {
            var parts = line.split('=>');
            if (parts.length < 2) return;
            var match = parts[0].trim().toLowerCase();
            var label = parts.slice(1).join('=>').trim();
            if (match && label) overrides.push({ match: match, label: label });
          });
      } catch (e) {}
    }

    function labelFor(heading) {
      var text = headingText(heading).replace(/\s+/g, ' ').trim();
      var lower = text.toLowerCase();
      for (var i = 0; i < overrides.length; i++) {
        if (lower.indexOf(overrides[i].match) !== -1) return overrides[i].label;
      }
      return heading.getAttribute('data-toc') || text;
    }

    var used = {};
    var links = headings.map(function (heading) {
      var id = heading.id || slugify(heading.textContent) || 'section';
      var base = id;
      var n = 2;
      while (used[id] || (document.getElementById(id) && document.getElementById(id) !== heading)) {
        id = base + '-' + n++;
      }
      used[id] = true;
      heading.id = id;

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'vd-awt-toc__link';
      a.href = '#' + id;
      a.textContent = labelFor(heading);
      li.appendChild(a);
      list.appendChild(li);
      return a;
    });

    nav.hidden = false;

    var activeIndex = -1;
    var lockUntil = 0;

    function setActive(index) {
      if (index === activeIndex) return;
      activeIndex = index;
      links.forEach(function (link, i) {
        var on = i === index;
        link.classList.toggle('is-active', on);
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      // Keep the active link visible when the rail itself scrolls (long TOCs).
      var link = links[index];
      if (link && nav.scrollHeight > nav.clientHeight) {
        var top = link.offsetTop - nav.clientHeight / 2;
        nav.scrollTop = Math.max(0, top);
      }
    }

    function update() {
      if (Date.now() < lockUntil) return;
      var line = stickyTop(root) + 32;
      var index = 0;
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top - line <= 0) index = i;
        else break;
      }
      setActive(index);
    }

    list.addEventListener('click', function (event) {
      var link = event.target.closest('a');
      if (!link) return;
      var target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      setActive(links.indexOf(link));
      lockUntil = Date.now() + 900; // don't let scroll-spy flicker during the smooth scroll
      scrollToTarget(target);
      if (history.replaceState) history.replaceState(null, '', link.hash);
    });

    update();
    return update;
  }

  // In-content anchor links (e.g. "See the full side by side comparison")
  function initAnchors(root) {
    root.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link || link.closest('[data-vd-awt-toc]') || link.hash.length < 2) return;
      var target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      scrollToTarget(target);
    });
  }

  function initFaq(root) {
    root.querySelectorAll('.vd-awt-faq__item').forEach(function (item, i) {
      var question = item.querySelector('.vd-awt-faq__q');
      var answer = item.querySelector('.vd-awt-faq__a');
      if (!question) return;
      if (!question.hasAttribute('type')) question.setAttribute('type', 'button');
      if (answer) {
        answer.id = answer.id || 'vd-awt-faq-' + i;
        question.setAttribute('aria-controls', answer.id);
      }
      question.setAttribute('aria-expanded', item.classList.contains('is-open'));
      question.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        question.setAttribute('aria-expanded', open);
      });
    });
  }

  function initRefs(root) {
    root.querySelectorAll('.vd-awt-refs').forEach(function (refs) {
      var toggle = refs.querySelector('.vd-awt-refs__toggle');
      if (!toggle) return;
      if (!toggle.hasAttribute('type')) toggle.setAttribute('type', 'button');
      toggle.setAttribute('aria-expanded', refs.classList.contains('is-open'));
      toggle.addEventListener('click', function () {
        var open = refs.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open);
      });
    });
  }

  function initSliders(root) {
    root.querySelectorAll('.vd-awt-slider').forEach(function (slider) {
      var track = slider.querySelector('.vd-awt-slider__track');
      var prev = slider.querySelector('.vd-awt-slider__btn--prev');
      var next = slider.querySelector('.vd-awt-slider__btn--next');
      if (!track) return;

      function page() {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return track.clientWidth + gap;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      [prev, next].forEach(function (btn, i) {
        if (!btn) return;
        if (!btn.hasAttribute('type')) btn.setAttribute('type', 'button');
        btn.addEventListener('click', function () {
          track.scrollBy({ left: i === 0 ? -page() : page(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function initShare(root) {
    var share = root.querySelector('.vd-awt-share');
    var toggle = share && share.querySelector('.vd-awt-share__toggle');
    if (!toggle) return;
    toggle.addEventListener('click', function () {
      var open = share.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open);
    });
    document.addEventListener('click', function (event) {
      if (!share.contains(event.target) && share.classList.contains('is-open')) {
        share.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-vd-awt-sticky]');
    // Appears when the trigger comes onto the screen, then stays pinned to the bottom
    // for the rest of the page (incl. footer). Trigger = the heading named in the
    // "Show from heading" setting, else the first product card in the article.
    var trigger = null;
    var fromHeading = (bar && bar.getAttribute('data-vd-awt-sticky-from') || '').trim().toLowerCase();
    if (fromHeading) {
      trigger = Array.prototype.find.call(
        root.querySelectorAll('.vd-awt-content h2, .vd-awt-content h3'),
        function (h) {
          return headingText(h).replace(/\s+/g, ' ').trim().toLowerCase().indexOf(fromHeading) !== -1;
        }
      ) || null;
    }
    trigger = trigger || root.querySelector('.vd-awt-content .vd-awt-product');
    var hero = root.querySelector('[data-vd-awt-hero]');
    if (!bar || !(trigger || hero)) return null;

    return function update() {
      var show = trigger
        ? trigger.getBoundingClientRect().top < window.innerHeight
        : hero.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Keep the footer's last lines reachable above the bar
      document.body.style.paddingBottom = show ? bar.offsetHeight + 'px' : '';
    };
  }

  // Product-card image/badge and the founder's note photo/signature come from section
  // settings. Liquid places them when the article markup is intact; this fills any
  // slot it couldn't reach (e.g. the editor reformatted the tags).
  function initInjectedMedia(root) {
    var cardTpl = root.querySelector('template[data-vd-awt-card-media]');
    if (cardTpl) {
      root.querySelectorAll('.vd-awt-product__media, .vd-awt-offer__media').forEach(function (slot) {
        if (slot.querySelector('.vd-awt-media-img, .iframe-frontrow-sticker')) return;
        slot.setAttribute('data-vd-awt-media', '');
        slot.insertBefore(cardTpl.content.cloneNode(true), slot.firstChild);
      });
    }

    var noteTpl = root.querySelector('template[data-vd-awt-note-media]');
    if (noteTpl) {
      root.querySelectorAll('.vd-awt-note').forEach(function (note) {
        if (note.querySelector('.vd-awt-injected')) return;
        var caption = note.querySelector('.vd-awt-note__caption');
        var media = noteTpl.content.cloneNode(true);
        if (caption) note.insertBefore(media, caption);
        else note.appendChild(media);
      });
    }
  }

  // Shop now buttons in the article HTML take their link from the section setting,
  // so a link the article editor mangled can't send people to a 404.
  function initShopLinks(root) {
    var url = root.getAttribute('data-vd-awt-shop-url');
    if (!url) return;
    root.querySelectorAll('.vd-awt-content a.vd-awt-btn').forEach(function (link) {
      link.setAttribute('href', url);
      link.removeAttribute('target');
    });
  }

  function init(root) {
    if (root.hasAttribute('data-vd-awt-ready')) return;
    root.setAttribute('data-vd-awt-ready', '');

    initInjectedMedia(root);
    initShopLinks(root);

    var updates = [initToc(root), initSticky(root)].filter(Boolean);
    initMobileHeadings(root);
    initAnchors(root);
    initFaq(root);
    initRefs(root);
    initSliders(root);
    initShare(root);

    if (!updates.length) return;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        updates.forEach(function (fn) {
          fn();
        });
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }

  function initAll() {
    document.querySelectorAll('[data-vd-awt]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', initAll);
})();
